import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TextField, Button, Box, Typography, Alert, Slider } from '@mui/material';
import { startTrade, setError } from '../../store/Slices/aiTradingSlice';
import { fetchAIBalances } from '../../store/Slices/assetSlice';

const ManualTradeForm = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const { aiBalances } = useSelector((state) => state.assets);
  const { loading, error } = useSelector((state) => state.aiTrading);
  const { light } = useSelector((state) => state.theme);
  const balance = aiBalances[0]?.balance ?? 0;
  const assetSymbol = aiBalances[0]?.asset_id?.symbol ?? 'USDT';
  const [investment, setInvestment] = useState('');
  const [leverage, setLeverage] = useState(1);
  const [tradeCount, setTradeCount] = useState('');
  const [localError, setLocalError] = useState('');

  const leverageMarks = [
    { value: 1, label: '1x' },
    { value: 25, label: '25x' },
    { value: 50, label: '50x' },
    { value: 75, label: '75x' },
    { value: 100, label: '100x' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    dispatch(setError(null));

    if (!user.ai_wallet) {
      setLocalError('AI wallet not configured. Please contact support.');
      return;
    }
    if (!investment || !leverage || !tradeCount) {
      setLocalError('Please fill in all fields');
      return;
    }
    const totalDeduction = parseFloat(investment) * 0;
    if (totalDeduction > balance) {
      setLocalError(`Investment + 0% fee (${totalDeduction.toFixed(2)} ${assetSymbol}) exceeds available balance (${balance.toFixed(2)} ${assetSymbol})`);
      return;
    }
    if (leverage < 1 || leverage > 100) {
      setLocalError('Leverage must be between 1 and 100');
      return;
    }
    if (parseInt(tradeCount) < 1) {
      setLocalError('Trade count must be 1 or greater');
      return;
    }

    try {
      const tradeAction = await dispatch(
        startTrade({
          user_id: user._id,
          ai_wallet_id: user.ai_wallet,
          investment: parseFloat(investment),
          leverage: parseInt(leverage),
          tradeCount: parseInt(tradeCount),
          tradeType: 'Manual',
        })
      );

      if (tradeAction.meta.requestStatus === 'fulfilled') {
        console.log('Manual trade successful, refreshing balance');
        setInvestment('');
        setLeverage(1);
        setTradeCount('');
        await dispatch(fetchAIBalances());
        console.log('Balance refreshed after manual trade');
      } else {
        setLocalError('Failed to start trade. Please try again.');
        console.error('Manual trade failed:', tradeAction.payload);
      }
    } catch (err) {
      setLocalError('Error processing trade. Please try again.');
      console.error('Manual trade error:', err);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      {(error || localError) && <Alert severity="error">{error || localError}</Alert>}
      {/* <Typography variant="body2" sx={{ color: light ? 'text.secondary' : '#b0b0b0' }}>
        Note: A 5% fee will be deducted from the investment amount.
      </Typography> */}
      <TextField
        label={`Investment (${assetSymbol})`}
        type="number"
        value={investment}
        onChange={(e) => setInvestment(e.target.value)}
        fullWidth
        variant="outlined"
        InputProps={{ inputProps: { min: 0, step: 0.01 } }}
        sx={{ bgcolor: light ? '#ecf0f1' : '#3c3c3c', '& .MuiInputBase-input': { color: light ? '#1a1a1a' : '#fff' } }}
        disabled={loading}
      />
      <Box>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: light ? '#1a1a1a' : '#fff' }}>
          Leverage (1-100x)
        </Typography>
        <Slider
          value={leverage}
          onChange={(e, newValue) => setLeverage(newValue)}
          min={1}
          max={100}
          step={1}
          marks={leverageMarks}
          valueLabelDisplay="auto"
          sx={{
            color: '#F3BA2F',
            '& .MuiSlider-markLabel': { color: light ? '#1a1a1a' : '#fff' },
            '& .MuiSlider-rail': { bgcolor: light ? '#ecf0f1' : '#3c3c3c' },
            '& .MuiSlider-track': { bgcolor: '#F3BA2F' },
          }}
          disabled={loading}
        />
      </Box>
      <TextField
        label="Trade Count"
        type="number"
        value={tradeCount}
        onChange={(e) => setTradeCount(e.target.value)}
        fullWidth
        variant="outlined"
        InputProps={{ inputProps: { min: 1 } }}
        sx={{ bgcolor: light ? '#ecf0f1' : '#3c3c3c', '& .MuiInputBase-input': { color: light ? '#1a1a1a' : '#fff' } }}
        disabled={loading}
      />
      <Button
        type="submit"
        variant="outlined"
        sx={{
          borderColor: '#F3BA2F',
          color: '#F3BA2F',
          py: 1.5,
          fontWeight: 'medium',
          transition: 'all 0.3s',
          '&:hover': { bgcolor: '#F3BA2F', color: '#fff' },
          width: { xs: '100%', sm: 'auto' },
        }}
        disabled={loading || !user.ai_wallet}
      >
        Open Trade
      </Button>
    </Box>
  );
};

export default ManualTradeForm;